import { useEffect } from 'react';
import { BrowserRouter, Routes, Route, useLocation, Link } from 'react-router-dom';
import Lenis from 'lenis';
import { Toaster } from '@/components/ui/sonner';
import '@/App.css';
import { Nav } from '@/components/site/Nav';
import { Footer } from '@/components/site/Footer';
import Home from '@/pages/Home';
import Providers from '@/pages/Providers';
import Buyers from '@/pages/Buyers';
import CategoryPage from '@/pages/CategoryPage';
import ServicesDirectory from '@/pages/ServicesDirectory';
import About from '@/pages/About';
import Privacy from '@/pages/Privacy';
import Terms from '@/pages/Terms';
import Advertise from '@/pages/Advertise';
import AdminShowcase from '@/pages/AdminShowcase';
import Account from '@/pages/Account';
import Dashboard from '@/pages/Dashboard';
import BuyerDashboard from '@/pages/BuyerDashboard';
import ProviderDashboard from '@/pages/ProviderDashboard';
import FounderDashboard from '@/pages/FounderDashboard';
import ServiceRegister from '@/pages/ServiceRegister';
import ServiceManagerLanding from '@/pages/ServiceManagerLanding';
import GuidesIndex from '@/pages/GuidesIndex';
import GuidePage from '@/pages/GuidePage';
import Directory from '@/pages/Directory';
import ProviderProfile from '@/pages/ProviderProfile';
import { track } from '@/lib/api';

const ScrollAndTrack = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
    track('page_view');
  }, [pathname]);
  return null;
};

const MobileCTA = () => {
  const { pathname } = useLocation();
  if (pathname === '/providers' || pathname === '/buyers') return null;
  return (
    <>
      <div className="h-[76px] md:hidden" />
      <div data-testid="mobile-cta-bar" className="fixed inset-x-0 bottom-0 z-40 flex gap-2 border-t border-line bg-ink/95 p-3 backdrop-blur-sm md:hidden">
        <Link to="/providers" data-testid="mobile-cta-provider" className="flex flex-1 items-center justify-center bg-volt px-3 py-3 font-mono text-[11px] font-bold uppercase tracking-[0.12em] text-ink">
          Join as Provider
        </Link>
        <Link to="/buyers" data-testid="mobile-cta-buyer" className="flex flex-1 items-center justify-center border border-line px-3 py-3 font-mono text-[11px] font-bold uppercase tracking-[0.12em] text-parchment">
          Post an RFQ
        </Link>
      </div>
    </>
  );
};

const NotFound = () => (
  <div className="flex min-h-[70vh] flex-col items-start justify-center bg-ink px-6 lg:px-10" data-testid="not-found-page">
    <p className="mono-label text-volt">404</p>
    <h1 className="mt-4 font-display text-5xl font-black tracking-tight text-parchment">Off the drawing.</h1>
    <p className="mt-4 text-steel">That page doesn't exist. Head back to the marketplace.</p>
    <Link to="/" data-testid="not-found-home-link" className="mt-8 bg-volt px-6 py-3.5 font-mono text-xs font-bold uppercase tracking-[0.15em] text-ink transition-transform duration-200 hover:-translate-y-0.5">
      Back home
    </Link>
  </div>
);

function App() {
  useEffect(() => {
    const lenis = new Lenis({ duration: 1.1, smoothWheel: true });
    let raf;
    const loop = (t) => {
      lenis.raf(t);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
    };
  }, []);

  return (
    <div className="App min-h-screen bg-ink">
      <BrowserRouter>
        <ScrollAndTrack />
        <Nav />
        <main>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/providers" element={<Providers />} />
            <Route path="/buyers" element={<Buyers />} />
            <Route path="/services" element={<ServicesDirectory />} />
            <Route path="/guides" element={<GuidesIndex />} />
            <Route path="/guides/:slug" element={<GuidePage />} />
            <Route path="/directory" element={<Directory />} />
            <Route path="/directory/:slug" element={<ProviderProfile />} />
            <Route path="/services/:slug" element={<CategoryPage />} />
            <Route path="/services/:slug/:countryCode" element={<CategoryPage />} />
            <Route path="/about" element={<About />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/advertise" element={<Advertise />} />
            <Route path="/admin/showcase" element={<AdminShowcase />} />
            <Route path="/account" element={<Account />} />
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/dashboard/buyer" element={<BuyerDashboard />} />
            <Route path="/dashboard/provider" element={<ProviderDashboard />} />
            <Route path="/dashboard/services" element={<ServiceRegister />} />
            <Route path="/service-manager" element={<ServiceManagerLanding />} />
            <Route path="/admin/dashboard" element={<FounderDashboard />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </main>
        <Footer />
        <MobileCTA />
        <Toaster theme="dark" position="bottom-right" />
      </BrowserRouter>
    </div>
  );
}

export default App;
